import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSponsorshipApi } from '../../../Hooks/useSponsorshipApi';
import AdminLayout from '../../../Components/admin/shared/AdminLayout';

/**
 * Admin Contract Detail Page
 * Shows detailed contract information and management options
 */
const AdminContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { contracts } = useSponsorshipApi();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContract = async () => {
      if (!id) return;

      try {
        const contractData = await contracts.getById(id);
        setContract(contractData);
      } catch (error) {
        console.error('Failed to load contract:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContract();
  }, [id, contracts]);

  const handleStatusChange = async (action) => {
    try {
      await contracts.updateContractStatus(id, action);
      // Reload contract data
      const updatedContract = await contracts.getById(id);
      setContract(updatedContract);
    } catch (error) {
      console.error(`Failed to ${action} contract:`, error);
    }
  };

  const handleFeasibilityCheck = async () => {
    try {
      await contracts.runFeasibilityCheck(id);
      // Reload contract data
      const updatedContract = await contracts.getById(id);
      setContract(updatedContract);
    } catch (error) {
      console.error('Failed to run feasibility check:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'warning', label: 'Draft' },
      active: { color: 'success', label: 'Active' },
      paused: { color: 'warning', label: 'Paused' },
      completed: { color: 'info', label: 'Completed' },
      cancelled: { color: 'error', label: 'Cancelled' },
    };
    
    const config = statusConfig[status] || { color: 'default', label: status };
    return <span className={`admin-badge admin-badge--${config.color}`}>{config.label}</span>;
  };

  const getAvailableActions = (status) => {
    const actions = [];
    
    switch (status) {
      case 'draft':
        actions.push({ key: 'feasibility', label: 'Run Feasibility Check', action: handleFeasibilityCheck });
        if (contract?.feasibility?.is_feasible) {
          actions.push({ key: 'activate', label: 'Activate Contract', action: () => handleStatusChange('activate') });
        }
        break;
      case 'active':
        actions.push({ key: 'pause', label: 'Pause Contract', action: () => handleStatusChange('pause') });
        break;
      case 'paused':
        actions.push({ key: 'resume', label: 'Resume Contract', action: () => handleStatusChange('resume') });
        break;
      default:
        break;
    }
    
    return actions;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="loading-spinner">Loading contract details...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!contract) {
    return (
      <AdminLayout>
        <div className="admin-error">
          <h1>Contract Not Found</h1>
          <p>The contract you're looking for doesn't exist.</p>
          <button 
            className="admin-button admin-button--primary"
            onClick={() => navigate('/admin/contracts')}
          >
            Back to Contracts
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-contract-detail">
        <div className="admin-contract-detail__header">
          <div className="admin-contract-detail__title">
            <button 
              className="admin-button admin-button--ghost"
              onClick={() => navigate('/admin/contracts')}
            >
              ← Back to Contracts
            </button>
            <h1>Contract {contract.id}</h1>
            {getStatusBadge(contract.status)}
          </div>
          <div className="admin-contract-detail__actions">
            {getAvailableActions(contract.status).map((action) => (
              <button
                key={action.key}
                className="admin-button admin-button--primary"
                onClick={action.action}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>

        <div className="admin-contract-detail__content">
          <div className="admin-contract-detail__main">
            {/* Contract Overview */}
            <div className="admin-card">
              <div className="admin-card__header">
                <h2>Contract Overview</h2>
              </div>
              <div className="admin-card__content">
                <div className="admin-overview">
                  <div className="admin-overview__row">
                    <div className="admin-overview__item">
                      <label>Sponsor</label>
                      <div>{contract.sponsor_id}</div>
                    </div>
                    <div className="admin-overview__item">
                      <label>Status</label>
                      <div>{getStatusBadge(contract.status)}</div>
                    </div>
                    <div className="admin-overview__item">
                      <label>Investment Amount</label>
                      <div>${(contract.investment_amount || 0).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="admin-overview__row">
                    <div className="admin-overview__item">
                      <label>Coverage Scope</label>
                      <div>{contract.coverage_scope}</div>
                    </div>
                    <div className="admin-overview__item">
                      <label>Weeks Purchased</label>
                      <div>{contract.weeks_purchased}</div>
                    </div>
                    <div className="admin-overview__item">
                      <label>Weeks Delivered</label>
                      <div>{contract.weeks_delivered || 0}</div>
                    </div>
                  </div>
                  <div className="admin-overview__row">
                    <div className="admin-overview__item">
                      <label>Start Date</label>
                      <div>{new Date(contract.start_date).toLocaleDateString()}</div>
                    </div>
                    <div className="admin-overview__item">
                      <label>End Date</label>
                      <div>{new Date(contract.end_date).toLocaleDateString()}</div>
                    </div>
                    <div className="admin-overview__item">
                      <label>Currency</label>
                      <div>{contract.currency || 'USD'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Territories */}
            <div className="admin-card">
              <div className="admin-card__header">
                <h2>Territories</h2>
              </div>
              <div className="admin-card__content">
                {contract.territories && contract.territories.length > 0 ? (
                  <div className="admin-territories">
                    {contract.territories.map((territory) => (
                      <div key={territory.code} className="admin-territory-item">
                        <div className="admin-territory-item__code">{territory.code}</div>
                        <div className="admin-territory-item__name">{territory.name}</div>
                        <div className="admin-territory-item__country">{territory.country}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="admin-empty-state">
                    <p>No territories specified</p>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Metrics */}
            {contract.delivery_metrics && (
              <div className="admin-card">
                <div className="admin-card__header">
                  <h2>Delivery Metrics</h2>
                </div>
                <div className="admin-card__content">
                  <div className="admin-metrics">
                    <div className="admin-metrics__item">
                      <label>Total Impressions</label>
                      <div>{(contract.delivery_metrics.total_impressions || 0).toLocaleString()}</div>
                    </div>
                    <div className="admin-metrics__item">
                      <label>Total Clicks</label>
                      <div>{(contract.delivery_metrics.total_clicks || 0).toLocaleString()}</div>
                    </div>
                    <div className="admin-metrics__item">
                      <label>Click-Through Rate</label>
                      <div>{(contract.delivery_metrics.ctr || 0).toFixed(2)}%</div>
                    </div>
                    <div className="admin-metrics__item">
                      <label>Completion Rate</label>
                      <div>{(contract.delivery_metrics.completion_rate || 0).toFixed(2)}%</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="admin-contract-detail__sidebar">
            {/* Feasibility Results */}
            {contract.feasibility && (
              <div className="admin-card">
                <div className="admin-card__header">
                  <h2>Feasibility Results</h2>
                </div>
                <div className="admin-card__content">
                  <div className="admin-feasibility">
                    <div className="admin-feasibility__status">
                      <span className={`admin-badge admin-badge--${contract.feasibility.is_feasible ? 'success' : 'error'}`}>
                        {contract.feasibility.is_feasible ? 'Feasible' : 'Not Feasible'}
                      </span>
                    </div>
                    <div className="admin-feasibility__score">
                      <label>Confidence Score</label>
                      <div>{contract.feasibility.confidence_score || 0}%</div>
                    </div>
                    {contract.feasibility.issues && contract.feasibility.issues.length > 0 && (
                      <div className="admin-feasibility__issues">
                        <label>Issues</label>
                        <ul>
                          {contract.feasibility.issues.map((issue, index) => (
                            <li key={index}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {contract.feasibility.recommendations && contract.feasibility.recommendations.length > 0 && (
                      <div className="admin-feasibility__recommendations">
                        <label>Recommendations</label>
                        <ul>
                          {contract.feasibility.recommendations.map((rec, index) => (
                            <li key={index}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Contract Metadata */}
            <div className="admin-card">
              <div className="admin-card__header">
                <h2>Contract Metadata</h2>
              </div>
              <div className="admin-card__content">
                <div className="admin-metadata">
                  <div className="admin-metadata__item">
                    <label>Contract ID</label>
                    <div>{contract.id}</div>
                  </div>
                  <div className="admin-metadata__item">
                    <label>Created</label>
                    <div>{new Date(contract.created_at).toLocaleString()}</div>
                  </div>
                  <div className="admin-metadata__item">
                    <label>Last Updated</label>
                    <div>{new Date(contract.updated_at).toLocaleString()}</div>
                  </div>
                  {contract.activated_at && (
                    <div className="admin-metadata__item">
                      <label>Activated</label>
                      <div>{new Date(contract.activated_at).toLocaleString()}</div>
                    </div>
                  )}
                  {contract.completed_at && (
                    <div className="admin-metadata__item">
                      <label>Completed</label>
                      <div>{new Date(contract.completed_at).toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContractDetail;
